import React, { useState } from 'react';
import { Card, LoadingSpinner, Modal } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { MdGavel } from 'react-icons/md';

const STATUS_META = {
  submitted: { label: 'Submitted', color: '#6366F1', bg: '#EEF2FF', icon: FiClock       },
  waiting:   { label: 'Waiting',   color: '#F59E0B', bg: '#FFFBEB', icon: FiAlertCircle  },
  approved:  { label: 'Approved',  color: '#10B981', bg: '#ECFDF5', icon: FiCheckCircle  },
  rejected:  { label: 'Rejected',  color: '#EF4444', bg: '#FEF2F2', icon: FiXCircle      },
  canceled:  { label: 'Canceled',  color: '#94A3B8', bg: '#F8FAFC', icon: FiXCircle      },
};

const STATUS_ROWS = ['submitted', 'waiting', 'approved', 'rejected', 'canceled'];

const fmt = n => Number(n || 0).toLocaleString('en-US');

// ── Horizontal bar for one sales-unit row ─────────────────────────────────────
const UnitBar = ({ name, count, maxCount }) => {
  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
  return (
    <div className="tw-flex tw-items-center tw-gap-2">
      <span className="tw-text-xs tw-text-primary-700 tw-w-32 tw-truncate tw-flex-shrink-0" title={name}>{name}</span>
      <div className="tw-flex-1 tw-bg-primary-100 tw-rounded-xl tw-h-2 tw-overflow-hidden">
        <div className="tw-h-full tw-rounded-xl" style={{ width: `${pct}%`, backgroundColor: '#6366F1' }} />
      </div>
      <span className="tw-text-xs tw-font-bold tw-text-primary-700 tw-w-6 tw-text-right tw-flex-shrink-0">{count}</span>
    </div>
  );
};

UnitBar.propTypes = {
  name: PropTypes.string.isRequired, count: PropTypes.number.isRequired, maxCount: PropTypes.number.isRequired,
};

// ── Single pending-CSD waiver row ─────────────────────────────────────────────
const WaiverRow = ({ w }) => (
  <div className={`tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-2.5 tw-rounded tw-border ${
    w.overdue ? 'tw-border-red-200 tw-bg-red-50' : 'tw-border-amber-100 tw-bg-amber-50'
  }`}>
    <div className="tw-flex-shrink-0 tw-mt-0.5">
      {w.overdue
        ? <FiAlertTriangle size={12} className="tw-text-red-500" />
        : <FiClock size={12} className="tw-text-amber-500" />}
    </div>
    <div className="tw-flex-1 tw-min-w-0">
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap">
        <span className="tw-font-mono tw-text-xs tw-font-bold tw-text-primary-700">{w.waiver_number}</span>
        {w.project_number && <span className="tw-text-xs tw-text-gray-500">· {w.project_number}</span>}
        {w.overdue && (
          <span className="tw-text-xs tw-font-semibold tw-text-red-600 tw-bg-red-100 tw-px-1.5 tw-rounded-sm">Overdue</span>
        )}
      </div>
      <Link
        to={`/crm/projects/${w.deal_id}`}
        target="_blank"
        rel="noreferrer"
        className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-blue-700 hover:tw-underline tw-truncate"
      >
        <span className="tw-truncate">{w.deal_name || '—'}</span>
        <FiExternalLink size={10} className="tw-flex-shrink-0" />
      </Link>
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5 tw-text-xs tw-text-gray-500">
        <span className="tw-truncate">{w.company_name}</span>
        {w.commitment_date && (
          <span className={`tw-flex-shrink-0 ${w.overdue ? 'tw-text-red-600 tw-font-semibold' : ''}`}>
            · Due {w.commitment_date}
          </span>
        )}
      </div>
    </div>
  </div>
);

WaiverRow.propTypes = { w: PropTypes.object.isRequired };

// ── Main component ────────────────────────────────────────────────────────────
const PREVIEW_COUNT = 5;

const WaiverAnalysisChart = ({ data, loading, error }) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (loading) {
    return (
      <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-justify-center tw-h-32">
          <LoadingSpinner size="sm" message="Loading waiver data…" />
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
        <div className="tw-flex tw-items-center tw-justify-center tw-h-24 tw-text-gray-400 tw-text-sm">
          {error ? 'Failed to load waiver data' : 'No data available'}
        </div>
      </Card>
    );
  }

  const thisMonth    = data.this_month || {};
  const unitRows     = thisMonth.by_unit || [];
  const maxUnitCount = Math.max(1, ...unitRows.map(u => u.count));
  const items        = data.pending_csd_items || [];
  const preview      = items.slice(0, PREVIEW_COUNT);

  return (
    <>
      <Card className="tw-border tw-border-gray-200 tw-shadow-sm">
        {/* ── Header ── */}
        <div className="tw-px-5 tw-py-4 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-gap-2">
          <MdGavel className="tw-text-primary-500" size={18} />
          <h3 className="tw-font-bold tw-text-gray-900">Waiver Analysis</h3>
          {data.total > 0 && (
            <span className="tw-text-xs tw-text-gray-400 tw-bg-gray-100 tw-px-2 tw-py-0.5 tw-rounded-xl">
              {fmt(data.total)} in period
            </span>
          )}
        </div>

        <div className="tw-p-5 tw-space-y-5">

          {/* ── Row 1: This-month (left) + Status tiles (right) ── */}
          <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">

            {/* This-month box */}
            <div className="tw-rounded-lg tw-border tw-border-primary-100 tw-bg-primary-50 tw-p-4 tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center tw-gap-2">
                <FiCalendar size={13} className="tw-text-primary-500" />
                <span className="tw-text-xs tw-font-semibold tw-text-primary-700 tw-uppercase tw-tracking-wide">
                  {thisMonth.month_label || 'This Month'}
                </span>
                <div className="tw-ml-auto tw-flex tw-items-baseline tw-gap-1">
                  <span className="tw-text-2xl tw-font-extrabold tw-text-primary-700">{fmt(thisMonth.total)}</span>
                  <span className="tw-text-xs tw-text-primary-400">waivers</span>
                </div>
              </div>
              {unitRows.length === 0 ? (
                <p className="tw-text-xs tw-text-primary-300 tw-text-center tw-py-1">No waivers this month</p>
              ) : (
                <div className="tw-space-y-2">
                  {unitRows.map(u => (
                    <UnitBar key={u.unit_id} name={u.unit_name} count={u.count} maxCount={maxUnitCount} />
                  ))}
                </div>
              )}
            </div>

            {/* Status breakdown */}
            <div className="tw-flex tw-flex-col tw-gap-3">
              <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
                Status Breakdown (Period)
              </p>
              <div className="tw-grid tw-grid-cols-5 tw-gap-2">
                {STATUS_ROWS.map(key => {
                  const meta = STATUS_META[key];
                  const Icon = meta.icon;
                  return (
                    <div
                      key={key}
                      className="tw-flex tw-flex-col tw-items-center tw-gap-1.5 tw-rounded-lg tw-py-3 tw-px-1"
                      style={{ backgroundColor: meta.bg }}
                    >
                      <Icon size={15} style={{ color: meta.color }} />
                      <span className="tw-text-xl tw-font-extrabold" style={{ color: meta.color }}>
                        {fmt(data[key] || 0)}
                      </span>
                      <span className="tw-text-xs tw-text-gray-500 tw-text-center tw-leading-tight">{meta.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Row 2: Pending CSD clearance — fixed height preview ── */}
          <div>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
              <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
                Pending CSD Clearance
              </p>
              <div className="tw-flex tw-items-center tw-gap-2">
                {data.overdue_count > 0 && (
                  <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-red-600 tw-bg-red-50 tw-border tw-border-red-100 tw-px-2 tw-py-0.5 tw-rounded-xl">
                    <FiAlertTriangle size={10} />
                    {data.overdue_count} overdue
                  </span>
                )}
                <span className="tw-text-xs tw-font-bold tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-100 tw-px-2 tw-py-0.5 tw-rounded-xl">
                  {fmt(data.pending_csd_count)} total
                </span>
                {items.length > PREVIEW_COUNT && (
                  <button
                    type="button"
                    onClick={() => setModalOpen(true)}
                    className="tw-text-xs tw-font-semibold tw-text-blue-600 hover:tw-text-blue-800 tw-bg-blue-50 hover:tw-bg-blue-100 tw-border tw-border-blue-200 tw-px-2.5 tw-py-0.5 tw-rounded-xl tw-transition-colors tw-cursor-pointer"
                  >
                    View all {data.pending_csd_count}
                  </button>
                )}
              </div>
            </div>

            {items.length === 0 ? (
              <div className="tw-text-center tw-py-5 tw-text-gray-400 tw-text-sm tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-100">
                <FiCheckCircle size={20} className="tw-mx-auto tw-mb-1.5 tw-text-green-400" />
                No waivers pending CSD clearance
              </div>
            ) : (
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-2">
                {preview.map(w => <WaiverRow key={w.id} w={w} />)}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ── Full list Modal ── */}
      <Modal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        size="full"
        title={
          <div className="tw-flex tw-items-center tw-gap-2">
            <MdGavel className="tw-text-primary-500" size={16} />
            <span>Pending CSD Clearance</span>
            <span className="tw-text-sm tw-font-normal tw-text-gray-400">({fmt(data.pending_csd_count)} waivers)</span>
            {data.overdue_count > 0 && (
              <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-red-600 tw-bg-red-50 tw-px-2 tw-py-0.5 tw-rounded-xl">
                <FiAlertTriangle size={10} />
                {data.overdue_count} overdue
              </span>
            )}
          </div>
        }
      >
        <div className="tw-p-4 tw-overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-2">
            {items.map(w => <WaiverRow key={w.id} w={w} />)}
          </div>
        </div>
      </Modal>
    </>
  );
};

WaiverAnalysisChart.propTypes = {
  data:    PropTypes.object,
  loading: PropTypes.bool,
  error:   PropTypes.any,
};
WaiverAnalysisChart.defaultProps = {
  data:    null,
  loading: false,
  error:   null,
};

export default WaiverAnalysisChart;
